import { useMemo, useRef, useState } from "react";

import { withCardon } from "cardon";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { MdAdd, MdClose, MdDelete, MdEdit, MdMerge } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Col, Label, Row } from "reactstrap";
import * as Yup from "yup";

import useProjectChange from "~common/hooks/useProjectChange";
import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRInput from "~components/Generic/PRInput";
import PRLink from "~components/Generic/PRLink";
import PRModal from "~components/Generic/PRModal";
import PRTable from "~components/Generic/PRTable";
import PRTooltip from "~components/Generic/PRTooltip";
import { apiUrlChatbot, tableFilterStorageKey } from "~constants";
import DialogHelper from "~helpers/DialogHelper";
import { deleteIntentTag, mergeIntentTags } from "~store/dialogComponents/intentTags/actions";
import { selectCurrentBot, selectCurrentProject } from "~store/user/selectors";

const MergeTagModal = withCardon(
  function MergeTagModal({ get, projectId, chatbotId, tagIds }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
        name: "",
      },
      validationSchema: Yup.object({
        name: Yup.string().required(t("common.required")),
      }),
      onSubmit: async (values) => {
        const payload = {
          tag_ids: tagIds,
          new_tag: values.name,
        };
        try {
          await dispatch(mergeIntentTags(projectId, chatbotId, payload));
          get(true)(); // Close modal and indicate success
        } catch (error) {
          console.error("Merge failed", error);
        }
      },
    });

    return (
      <PRModal
        submitText={t("common.merge")}
        title={t("dashboard.intentTagsList.modalMerge.title").format(tagIds?.length)}
        zIndex={9999}
        onClick={formik.handleSubmit}
        onClose={get(false)}
      >
        <Row className="g-2">
          <Col xs={12}>
            <Label>{t("common.newName")}</Label>
            <PRInput
              invalid={formik.touched.name && formik.errors.name}
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </Col>
        </Row>
      </PRModal>
    );
  },
  {
    destroyOnHide: true,
  }
);

export default function IntentTagsList() {
  const { t } = useTranslation();
  const tableRef = useRef(null);
  const currentProject = useSelector(selectCurrentProject);
  const currentBot = useSelector(selectCurrentBot);

  const [selectedTagList, setSelectedTagList] = useState([]);
  const [checkableModeOnOff, setCheckableModeOnOff] = useState(false);
  const dispatch = useDispatch();
  useProjectChange(() => {
    tableRef.current.resetPagination();
  }, []);
  const columns = useMemo(() => {
    return [
      {
        label: t("common.name"),
        key: "key",
      },
      {
        label: t("common.count"),
        key: "intent_count",
      },
      {
        label: t("dashboard.intentTagsList.boundedIntents"),
        key: "intent_list",
        render: (row) => {
          const first20 = row.intent_list?.slice(0, 20);
          const restItems = row.intent_list?.slice(20);
          const restCount = row.intent_list?.length - 20;
          return (
            <>
              {first20?.map((item) => {
                return (
                  <Badge key={item.id} className="badge-soft-secondary me-1">
                    <PRLink newTab to={`/chatbot/intent/form/${item.id}/`}>
                      <div
                        className="font-size-12 text-truncate
                      "
                        style={{ maxWidth: 225 }}
                      >
                        <PRTooltip title={item.display_text || item.text}>{item.display_text || item.text}</PRTooltip>
                      </div>
                    </PRLink>
                  </Badge>
                );
              })}

              {restCount > 0 && (
                <Badge className="badge-soft-secondary me-1">
                  <div className="font-size-12 text-primary">
                    <PRTooltip
                      title={
                        <ul className="text-start">
                          {restItems.map((item) => (
                            <li key={item.id}>{item.display_text || item.text}</li>
                          ))}
                        </ul>
                      }
                    >
                      +{restCount}
                    </PRTooltip>
                  </div>
                </Badge>
              )}
            </>
          );
        },
      },
      {
        label: t("common.actions"),
        key: "actions",
        actions: true,
        render: (row) => {
          const handleClickDelete = async () => {
            if (!(await DialogHelper.showQuestionDeleteWithFormat(row.key))) return;
            dispatch(deleteIntentTag(currentProject.id, currentBot.id, row.id)).then(() => {
              tableRef.current.refresh();
            });
          };

          return (
            <div className="d-flex justify-content-center">
              <PRButton
                outline
                color="primary"
                icon={MdEdit}
                link={`/chatbot/intent-tags/form/${row.id}`}
                size="sm"
                tooltipText={t("common.edit")}
              />
              <PRButton
                outline
                className="ms-1"
                color="danger"
                icon={MdDelete}
                size="sm"
                tooltipText={t("common.delete")}
                onClick={handleClickDelete}
              />
            </div>
          );
        },
      },
    ];
  }, [dispatch, currentProject, currentBot, t]);

  const actions = useMemo(() => {
    if (checkableModeOnOff) {
      return [
        {
          label: t("dashboard.intentTagsList.merge"),
          icon: MdMerge,
          disabled: selectedTagList.length < 2,
          color: "primary",
          onClick: async () => {
            const result = await MergeTagModal.show({
              projectId: currentProject.id,
              chatbotId: currentBot.id,
              tagIds: selectedTagList,
            });
            if (result) {
              setCheckableModeOnOff(false);
              setSelectedTagList([]);
              tableRef.current.refresh();
            }
          },
        },
        {
          label: t("common.cancel"),
          icon: MdClose,
          color: "danger",
          onClick: () => {
            setCheckableModeOnOff(false);
            setSelectedTagList([]);
          },
        },
      ];
    }

    return [
      {
        label: t("common.createNew"),
        icon: MdAdd,
        color: "success",
        link: `/chatbot/intent-tags/form/`,
      },
      {
        label: t("dashboard.intentTagsList.tagMerge"),
        icon: MdEdit,
        color: "warning",
        onClick: () => setCheckableModeOnOff(true),
      },
    ];
  }, [checkableModeOnOff, selectedTagList, t, currentProject, currentBot]);

  const filters = useMemo(() => {
    return [
      {
        key: "bot",
        visible: false,
      },
      {
        key: "key",
        label: t("common.name"),
      },
    ];
  }, [t]);

  const handleRowClick = (id, checked) => {
    setSelectedTagList((prevSelected) => {
      if (checked) {
        const newSelected = new Set(prevSelected);
        newSelected.add(id);
        return Array.from(newSelected);
      } else {
        return prevSelected.filter((item) => item !== id);
      }
    });
  };

  const handleSelectAll = (idList) => {
    setSelectedTagList(idList);
  };

  const handleSelectRange = (idList, checked) => {
    setSelectedTagList((prevSelected) => {
      const newSelected = new Set(prevSelected);
      idList.forEach((id) => {
        if (checked) {
          newSelected.add(id);
        } else {
          newSelected.delete(id);
        }
      });
      return Array.from(newSelected);
    });
  };

  const parentName = [
    {
      label: t("common.intentTags"),
      url: "/chatbot/intent-tags",
    },
  ];

  return (
    <PRContainer
      smalltalkSelector
      actions={actions}
      description={t("dashboard.intentTagsList.description")}
      name={t("common.chatbot")}
      parentName={parentName}
    >
      <PRTable
        ref={tableRef}
        columns={columns}
        filters={filters}
        storageKey={tableFilterStorageKey.intentTagsList}
        url={apiUrlChatbot.getIntentTags.format(currentProject.id, currentBot.id)}
        {...(checkableModeOnOff && {
          selectedRows: selectedTagList,
          onRowChecked: handleRowClick,
          onRowClick: handleRowClick,
          onSelectAll: handleSelectAll,
          onSelectRange: handleSelectRange,
        })}
      />
    </PRContainer>
  );
}
